import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import type { StepClienteValues } from '@/features/quotes/schemas/rd-equipamentos/step-cliente.schema';
import type { RdQuoteStepOnNextResult } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import type { RdClientPayload } from '@/services/quotes/rd-equipamentos/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { useCreateRdProposal } from './use-create-rd-proposal.hook';
import { useUpdateRdClient } from './use-update-rd-client.hook';
import { normalizeEmail } from '@/shared/utils/normalize-email.util';

/** Campos com validador `onChange` próprio — precisam ser revalidados antes do submit. */
const VALIDATED_FIELDS: (keyof StepClienteValues)[] = [
  'insuredDoc',
  'insuredName',
  // Celular e e-mail passaram a ser obrigatórios no formulário (o backend
  // segue aceitando ausentes). É esta lista, e não o schema Zod, que o submit
  // revalida — sem entrar aqui, a obrigatoriedade só existiria enquanto o
  // usuário digitasse no campo.
  'insuredPhone',
  'insuredEmail',
  'validityStart',
  'validityEnd',
];

type FieldMetaLike = { errorMap?: Record<string, unknown> };

/**
 * Superfície mínima de `useAppForm` (TanStack Form) usada por este hook —
 * evita tipar os ~20 parâmetros genéricos de `FormApi`, mesma técnica de
 * `use-submit-capital-global-step1.hook.ts`.
 */
/* eslint-disable no-unused-vars -- Numa ASSINATURA de tipo os nomes dos
   parâmetros não são variáveis: documentam o contrato de quem implementa
   (é o `setFieldMeta` do TanStack Form). A regra os lê como declarações
   não usadas; o desligamento vale só para este bloco de tipos. */
type SubmitForm = {
  state: { values: StepClienteValues };
  validateField: (
    field: keyof StepClienteValues,
    cause: 'change' | 'blur' | 'submit'
  ) => Promise<unknown[]>;
  /**
   * Retorna `undefined` para campos que o TanStack Form ainda não registrou
   * (ex.: `insuredRgDate` com `insuredType: 'cnpj'`, `mailState` com o
   * accordion de correspondência fechado) — usado para nunca chamar
   * `setFieldMeta` num campo desmontado (ver uso abaixo).
   */
  getFieldMeta: (field: keyof StepClienteValues) => FieldMetaLike | undefined;
  setFieldMeta: (
    field: keyof StepClienteValues,
    updater: (meta: FieldMetaLike | undefined) => FieldMetaLike
  ) => void;
};
/* eslint-enable no-unused-vars */

type UseSubmitRdClienteParams = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ver nota em `form` abaixo
  form: any;
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  onNext: (result?: RdQuoteStepOnNextResult) => void;
};

function buildPayload(v: StepClienteValues): RdClientPayload {
  const isCpf = v.insuredType === 'cpf';
  const hasPreviousPolicy = v.policyType !== 'novo';

  return {
    productType: v.productType,
    // Atributo de subscrição (PO 2026-07-20) — não altera o cálculo; o motor
    // usa para acionar as cláusulas obrigatórias da apólice. Não força mais
    // análise manual (o referral saiu de `Pricing._leased_referral`).
    leasedToThirdParties: v.leasedToThirdParties,
    insuredType: v.insuredType,
    insuredDoc: v.insuredDoc,
    insuredName: v.insuredName,
    insuredSocialName: isCpf ? v.insuredSocialName || '' : '',
    insuredRg: isCpf ? v.insuredRg || '' : '',
    insuredRgIssuer: isCpf ? v.insuredRgIssuer || '' : '',
    // DATE field: `@IsOptional() @IsDateString()` no backend não trata ''
    // como ausente — precisa ser omitido (undefined), não ''. Diferente dos
    // campos de texto acima, que mantêm o clear-on-edit via ''.
    insuredRgDate: isCpf ? v.insuredRgDate || undefined : undefined,
    insuredPhone: v.insuredPhone || '',
    insuredLandline: v.insuredLandline || '',
    // EMAIL: `@IsOptional() @IsEmail()` no backend não trata '' como
    // ausente — precisa ser omitido (undefined), não ''. Mesmo caso do
    // `insuredRgDate` e do `riskState` acima.
    insuredEmail: normalizeEmail(v.insuredEmail) || undefined,
    brokerName: v.brokerName || '',
    policyType: v.policyType,
    previousPolicy: hasPreviousPolicy ? v.previousPolicy || '' : '',
    riskCep: v.riskCep || '',
    riskAddress: v.riskAddress || '',
    riskNumber: v.riskNumber || '',
    riskComplement: v.riskComplement || '',
    riskDistrict: v.riskDistrict || '',
    riskCity: v.riskCity || '',
    // `@IsOptional() @Length(2, 2)` no backend (RdClientDto) — mesmo padrão
    // do `insuredRgDate`: '' não é tratado como ausente e falha o Length.
    // Precisa ser omitido, não ''.
    riskState: v.riskState || undefined,
    mailCep: v.mailCep || '',
    mailAddress: v.mailAddress || '',
    mailNumber: v.mailNumber || '',
    mailComplement: v.mailComplement || '',
    mailDistrict: v.mailDistrict || '',
    mailCity: v.mailCity || '',
    mailState: v.mailState || undefined,
    coverageScope: v.coverageScope,
    validityStart: v.validityStart,
    validityEnd: v.validityEnd,
  };
}

export function useSubmitRdCliente({
  form: formRaw,
  onNext,
}: UseSubmitRdClienteParams) {
  const form = formRaw as SubmitForm;
  const queryClient = useQueryClient();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();
  const hasProposalId = Boolean(resolvedProposalId);

  const { mutateAsync: createAsync, isPending: isCreating } =
    useCreateRdProposal();
  const { mutateAsync: updateAsync, isPending: isUpdating } =
    useUpdateRdClient();

  async function handleSubmit() {
    setSubmitAttempted(true);

    const results = await Promise.all(
      VALIDATED_FIELDS.map(field => form.validateField(field, 'change'))
    );
    if (results.some(r => Array.isArray(r) && r.length > 0)) return;

    const payload = buildPayload(form.state.values);

    try {
      if (hasProposalId) {
        const updated = await updateAsync({
          proposalId: resolvedProposalId,
          payload,
        });
        await queryClient.refetchQueries({
          queryKey: ['rd-proposal', resolvedProposalId],
        });
        if (updated) onNext();
        return;
      }

      const created = await createAsync(payload);
      if (created?.proposalId) {
        onNext({
          proposalId: created.proposalId,
          productType: created.productType ?? form.state.values.productType,
        });
      }
    } catch (error) {
      if (error instanceof ValidationError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, fieldError]) => {
          const fieldKey = field as keyof StepClienteValues;
          // Campos ainda não montados (ex.: `insuredRgDate` quando
          // `insuredType` é 'cnpj', `mailState` com o accordion de
          // correspondência fechado) não têm meta registrada — TanStack
          // Form chamaria o updater com `meta === undefined`, e acessar
          // `meta.errorMap` quebraria o fluxo de submit inteiro. Só
          // aplicamos o erro inline em campos conhecidos pelo form; os
          // demais já são comunicados pelo toaster abaixo.
          if (form.getFieldMeta(fieldKey) === undefined) return;
          try {
            form.setFieldMeta(fieldKey, meta => ({
              ...meta,
              errorMap: { ...meta?.errorMap, onSubmit: fieldError.message },
            }));
          } catch {
            // Nunca deixa um erro de mapeamento de campo derrubar o
            // fluxo de submit — o toaster abaixo já comunica o erro.
          }
        });
      }

      const description =
        error instanceof ValidationError
          ? error.detail?.trim() ||
            (error.messages.length ? error.messages.join('\n') : error.message)
          : error instanceof Error
            ? error.message
            : 'Erro inesperado';

      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar',
        description,
      });
    }
  }

  return {
    handleSubmit,
    isPending: isCreating || isUpdating,
    submitAttempted,
    hasProposalId,
  };
}
